'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import { ContactInformation } from '@/components/checkout/ContactInformation';
import { parseStoredPhoneNumber } from '@/lib/checkout/express/parseStoredPhoneNumber';
import { getCountryFromLocale } from '@/lib/i18n';
import { isValidPhoneNumber } from '@/lib/phone';
import type { ServiceAddress } from '@/lib/service/ecs/contracts';
import { formatRepairContactPhone } from '@/lib/service/repairRequestReview/mapStoredToServiceAddress';
import type { RepairDetailsContactValues } from '@/lib/service/repairDetailsUpdate';

import { RepairDetailsContactSummary } from './RepairDetailsContactSummary';
import { RepairDetailsSection } from './RepairDetailsSection';

interface RepairDetailsContactSectionProps {
  shippingAddress: ServiceAddress;
  canChange: boolean;
  startInEditMode: boolean;
  isProcessing: boolean;
  onSubmit: (values: RepairDetailsContactValues) => Promise<void>;
}

export function RepairDetailsContactSection({
  shippingAddress,
  canChange,
  startInEditMode,
  isProcessing,
  onSubmit,
}: Readonly<RepairDetailsContactSectionProps>) {
  const locale = useLocale();
  const tPersonal = useTranslations('service.repairRequest.personal');
  const tCheckout = useTranslations('checkout');
  const countryCode = getCountryFromLocale(locale).toUpperCase();
  const phoneNumber = shippingAddress.phoneNumber || shippingAddress.mobileNumber;
  const parsedPhone = useMemo(
    () => parseStoredPhoneNumber(phoneNumber, countryCode),
    [countryCode, phoneNumber]
  );
  const [email, setEmail] = useState(shippingAddress.email);
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const phoneMethods = useForm({
    defaultValues: {
      phonePrefix: parsedPhone.phonePrefix,
      phone: parsedPhone.phone,
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      nextErrors.email = tCheckout('delivery.requiredField');
    }

    const phonePrefixCode = phoneMethods.getValues('phonePrefix');
    const phone = phoneMethods.getValues('phone');
    const fullPhone = formatRepairContactPhone(phonePrefixCode, phone);

    if (!phone.trim()) {
      nextErrors.phoneNumber = tCheckout('delivery.requiredField');
    } else if (!isValidPhoneNumber(fullPhone)) {
      nextErrors.phoneNumber = tCheckout('delivery.invalidPhoneNumber');
    }

    setContactErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    await onSubmit({
      email: trimmedEmail,
      phonePrefixCode,
      phone,
    });
  };

  return (
    <RepairDetailsSection
      title={tPersonal('contact.title')}
      testId="repair-details-contact"
      canChange={canChange}
      startInEditMode={startInEditMode}
      isProcessing={isProcessing}
      summary={
        <RepairDetailsContactSummary email={shippingAddress.email} phoneNumber={phoneNumber} />
      }
    >
      {({ onCancel }) => (
        <form
          id="repair-details-contact-form"
          data-testid="repair-details-contact-form"
          noValidate
          onSubmit={(event) => {
            void handleSubmit(event).catch(() => undefined);
          }}
          onReset={onCancel}
        >
          <ContactInformation
            embedded
            email={email}
            emailError={contactErrors.email}
            phoneError={contactErrors.phoneNumber}
            onEmailChange={setEmail}
            enableEmailCheck={false}
            showHeader={false}
            showDescription={false}
            phoneMethods={phoneMethods}
            lang={locale}
            defaultCountryCode={countryCode}
            isSubmitting={isProcessing}
          />
        </form>
      )}
    </RepairDetailsSection>
  );
}
